import React, { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { fmtIDR } from "@/lib/format";

const RekeningKoran = () => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [txs, setTxs] = useState([]);
  const [coa, setCoa] = useState([]);
  const [bankAccount, setBankAccount] = useState("1102");

  useEffect(() => { api.get("/coa").then((r) => setCoa(r.data)); }, []);

  const parse = async () => {
    if (!file) { toast.error("Pilih file PDF terlebih dahulu"); return; }
    setParsing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("spbu_token");
      const outlet = localStorage.getItem("spbu_active_outlet") || "default";
      const res = await fetch(`${API}/import/rekening-koran`, {
        method: "POST", body: form,
        headers: { Authorization: `Bearer ${token}`, "X-Outlet-Id": outlet },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Gagal parse");
      }
      const data = await res.json();
      setTxs(data.transactions || []);
      toast.success(`${data.transactions?.length || 0} transaksi terdeteksi`);
    } catch (e) { toast.error(e.message); }
    finally { setParsing(false); }
  };

  const updateTx = (rowId, key, val) => {
    setTxs((prev) => prev.map((t) => t.row_id === rowId ? { ...t, [key]: val } : t));
  };

  const commit = async () => {
    const valid = txs.filter((t) => t.suggested_account);
    if (valid.length === 0) { toast.error("Belum ada transaksi valid"); return; }
    setCommitting(true);
    try {
      const payload = {
        bank_account: bankAccount,
        transactions: valid.map((t) => ({
          row_id: t.row_id, date: t.date, description: t.description,
          debit: t.debit, credit: t.credit, account: t.suggested_account,
        })),
      };
      const r = await api.post("/import/rekening-koran/commit", payload);
      toast.success(`${r.data.created} jurnal dibuat (status: menunggu approval)`);
      setTxs([]); setFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal commit"); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Import Rekening Koran (PDF)</h1>
        <p className="text-sm text-slate-500 mt-1">Upload PDF rekening koran → transaksi diekstrak otomatis oleh AI → review → jadi jurnal pending untuk approval</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><Upload className="w-5 h-5" />1. Upload File PDF</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>File Rekening Koran (PDF)</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="rekkor-file-input" />
            </div>
            <div>
              <Label>Akun Bank (Debit/Kredit lawan)</Label>
              <Select value={bankAccount} onValueChange={setBankAccount}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {coa.filter((c) => c.code === "1101" || c.code === "1102").map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={parse} disabled={parsing || !file} className="bg-[#005DAA] hover:bg-[#004885]" data-testid="rekkor-parse-button">
            {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {parsing ? "Membaca PDF & mengekstrak transaksi..." : "Parse PDF"}
          </Button>
        </CardContent>
      </Card>

      {txs.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold">2. Review Transaksi ({txs.length})</CardTitle>
            <Button onClick={commit} disabled={committing} className="bg-[#E30613] hover:bg-[#C8040F]" data-testid="rekkor-commit-button">
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Kirim ke Approval
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Kredit</TableHead>
                <TableHead>Akun Lawan (COA)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {txs.map((t) => (
                  <TableRow key={t.row_id} data-testid={`rekkor-tx-${t.row_id}`}>
                    <TableCell className="font-mono-fig text-sm">{t.date}</TableCell>
                    <TableCell className="text-sm max-w-md">{t.description}</TableCell>
                    <TableCell className="text-right font-mono-fig text-rose-700">{t.debit > 0 ? fmtIDR(t.debit) : ""}</TableCell>
                    <TableCell className="text-right font-mono-fig text-emerald-700">{t.credit > 0 ? fmtIDR(t.credit) : ""}</TableCell>
                    <TableCell>
                      <Select value={t.suggested_account} onValueChange={(v) => updateTx(t.row_id, "suggested_account", v)}>
                        <SelectTrigger className="h-8 w-64"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                        <SelectContent>
                          {coa.filter((c) => c.code !== bankAccount).map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <b>Catatan:</b> AI otomatis menyarankan akun beban (6107) untuk debit dan kas (1101) untuk kredit. Silakan sesuaikan akun lawan sebelum kirim. Semua jurnal akan berstatus <b>pending</b> hingga admin menyetujui.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RekeningKoran;
